import { DEFAULT_PAGE_SIZE, pageRange, type PageMeta } from '@palaccount/contracts';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

/**
 * THE SHARED PAGING HOOK — the one `features/audit/PAGINATION.md` argued for.
 *
 * EC-19-04 — "very large": every list loads a block at a time (NFR-42's
 * product-wide conversion rides this hook; the settled block is
 * DEFAULT_PAGE_SIZE = 50 where the EC PROPOSED 25 — the paging waves'
 * convention, recorded), tables scroll inside their own card (GEN-22/
 * NFR-15), and search boxes arrived with GEN-50's typeahead.
 *
 * "Doing it once, as a shared `usePagedQuery` hook backed by search params, is
 * much cheaper than doing it forty times by hand afterwards."
 *
 * DSH-09 keeps its page in component state, which means a link to "page 3 of
 * the audit log" does not exist and a refresh returns to page 1. Acceptable for
 * an investigator's session; wrong for a report somebody sends their
 * accountant. `router.tsx`'s own header anticipated this — "the URL carries
 * filters and pagination later (§2.6)".
 *
 * ── THE FOUR PROPERTIES, IN ONE PLACE ──────────────────────────────────────
 *
 * 1. THE PAGE LIVES IN THE URL (`?page=3`). It survives a reload, a back
 *    button and a pasted link. `replace: true` on every write, so paging
 *    through 40 pages leaves ONE history entry rather than forty — pressing
 *    Back after reading a list should leave the list, not walk it backwards.
 *
 * 2. PAGE 1 IS ABSENT FROM THE URL. `?page=1` and no parameter mean the same
 *    thing, so the canonical link to a list has no query string at all and two
 *    links to the same page are the same string.
 *
 * 3. ANY FILTER CHANGE RESETS TO PAGE 1 (BR-DSH-09-19), through `resetPage()`
 *    wrapped around the setters — NOT an effect on the filter values. An
 *    effect fires on the first render too, and would fight the whole point of
 *    property 1 by throwing away the page the URL just asked for.
 *
 * 4. THE PAGE SIZE IS NOT IN THE URL. It is a property of the screen, not of
 *    the thing being linked to; putting it in the link is how a shared URL
 *    ends up fetching 500 rows into somebody else's browser.
 *
 * ── A SCREEN WITH MORE THAN ONE LIST ON IT ────────────────────────────────
 *
 * Every screen this hook was written for has one list, so its page is `?page=`
 * and the name never had to be a decision. MFG-03 is the first with FOUR —
 * demand, net requirements, reorder targets and the schedule are four blocks
 * of one screen (§4), not four screens — and one `?page=` for four pagers
 * would move all four at once.
 *
 * So the parameter NAME is an argument: `usePagedState(50, 'demand')` keeps its
 * page in `?demandPage=`. It stays optional, and the default stays `page`,
 * because naming it on a one-list screen would only make forty screens carry a
 * word that says nothing. Property 2 holds per list: page 1 deletes its own
 * parameter, so the canonical link to MFG-03 is still `/manufacturing/planning`
 * with no query string, whichever block you scrolled first.
 */
export interface PagedState {
  /** 1-based, read from `?page=`; 1 when absent or nonsense. */
  page: number;
  /** The block size this screen asks for. */
  pageSize: number;
  /** Go to a page. Clamps at 1; page 1 clears the parameter. */
  setPage: (page: number) => void;
  /**
   * Wrap a filter setter so changing it returns to page 1 (BR-DSH-09-19).
   * `onChange={change(setStatus)}` reads the same as `onChange={setStatus}`,
   * which is what makes it survive being copied into the next screen.
   */
  change: <T>(setter: (value: T) => void) => (value: T) => void;
  /** Drop back to page 1 without changing anything else. */
  resetPage: () => void;
}

export function usePagedState(
  pageSize: number = DEFAULT_PAGE_SIZE,
  /** The list's own name, for a screen carrying more than one — see the header. */
  name?: string,
): PagedState {
  const [params, setParams] = useSearchParams();
  const key = name === undefined ? 'page' : `${name}Page`;

  // A hand-edited `?page=abc`, `?page=0` or `?page=-3` is a page 1, not an
  // error page: a list is not the place to punish a bad link.
  const raw = Number(params.get(key));
  const page = Number.isFinite(raw) && raw >= 1 ? Math.floor(raw) : 1;

  const setPage = useCallback(
    (next: number) => {
      setParams(
        (current) => {
          const updated = new URLSearchParams(current);
          if (next <= 1) updated.delete(key);
          else updated.set(key, String(Math.floor(next)));
          return updated;
        },
        { replace: true },
      );
    },
    [setParams, key],
  );

  const resetPage = useCallback(() => setPage(1), [setPage]);

  const change = useCallback(
    <T,>(setter: (value: T) => void) =>
      (value: T) => {
        setter(value);
        setPage(1);
      },
    [setPage],
  );

  return useMemo(
    () => ({ page, pageSize, setPage, change, resetPage }),
    [page, pageSize, setPage, change, resetPage],
  );
}

/**
 * The numbers a pager draws, from the meta a paged response already carries.
 * `pageRange` itself lives in the contracts package so the server and the
 * screen cannot disagree about what "Showing 1–8 of 12" means.
 */
export function usePageRange(meta: PageMeta | undefined, fallbackPageSize: number) {
  return useMemo(() => {
    const resolved = meta ?? { total: 0, page: 1, pageSize: fallbackPageSize };
    return { ...pageRange(resolved), total: resolved.total };
  }, [meta, fallbackPageSize]);
}
